import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import {
  Autocomplete,
  Paper,
  Stack,
  TableCell,
  TableContainer,
  TableHead,
  TextField,
  TableRow,
  Table,
  TableBody
} from "@mui/material";
import { FIELD_TYPES } from "../../utility/constants";
import { intersection } from "../../utility/utilityFunctions";

function ReportView() {
  let { process_template_id } = useParams();
  const processTemplateId = parseInt(process_template_id);
  const companyId = 10;

  const { user } = useAuthContext();
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL;

  let axiosOptions = {};
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` }
    };
  }

  const [reportFilterSelectedValues, setReportFilterSelectedValues] = useState(
    {}
  );
  const [reportData, setReportData] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [allReferenceNumbers, setAllReferenceNumbers] = useState([]);

  //set initial report data - this should happen only once
  useEffect(() => {
    const getDataFromServer = async () => {
      const reportData = (
        await axios.get(
          `${NODE_BASE_URL}/report/report-data/boq-data-report`,
          axiosOptions
        )
      ).data;

      setReportData(reportData);

      let allRefNums = Array.from(
        new Set(
          reportData.data.map(
            (d) =>
              reportData.processTemplateData.prefix +
              "-" +
              d.process_reference_num
          )
        )
      );

      setAllReferenceNumbers(allRefNums);
    };

    //processing starts here
    getDataFromServer();
  }, []);

  //set table data based on filters
  useEffect(() => {
    if (
      reportFilterSelectedValues == null ||
      Object.keys(reportFilterSelectedValues)?.length == 0
    ) {
      setFilteredData([]);
    } else {
      let newFilterData2D = [];

      for (let [filterKey, filterValues] of Object.entries(
        reportFilterSelectedValues
      )) {
        let newFilterData = [];

        if (filterValues.length == 0) {
          continue;
        }
        filterKey = filterKey.split("__")[0];
        reportData.data.filter((reportDatum) => {
          if (filterValues.includes(Number(reportDatum[filterKey]))) {
            newFilterData.push(JSON.stringify(reportDatum));
          }
        });

        newFilterData2D.push(newFilterData);
      }

      let intersect = intersection(newFilterData2D);

      intersect = intersect.map((elem) => JSON.parse(elem));

      setFilteredData(intersect);
    }
  }, [reportFilterSelectedValues]);

  const autocompleteRef = useRef({});
  autocompleteRef.current = {};
  const getEachAutocompleteRef = (fieldId, el) => {
    if (el && !Object.values(autocompleteRef.current).includes(el)) {
      autocompleteRef.current[fieldId] = el;
    }
  };

  const handleFilterChange = (fieldId, _e, value) => {
    let targetValueIds = value.map((val) => val.id);
    let targetName = autocompleteRef.current[fieldId].getAttribute("name");
    setReportFilterSelectedValues({
      ...reportFilterSelectedValues,
      [targetName]: targetValueIds
    });
  };

  let tableDataToShow;

  if (
    (filteredData == null || Object.keys(filteredData)?.length == 0) &&
    isFiltersEmpty(reportFilterSelectedValues)
  ) {
    tableDataToShow = reportData?.data;
  } else {
    tableDataToShow = filteredData;
  }

  return (
    <>
      <Stack spacing={3} sx={{ width: "90%" }}>
        {/* <Autocomplete
          ref={(el) => getEachAutocompleteRef(`ref_num`, el)}
          options={allReferenceNumbers}
          name={`ref_num`}
          id={`ref_num`}
          multiple
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(e, v) => handleFilterChange(`ref_num`, e, v)}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label={allReferenceNumbers} />
          )}
        /> */}
        {reportData.filterFields?.map((field) => (
          <Autocomplete
            ref={(el) => getEachAutocompleteRef(`f_${field.id}`, el)}
            options={field.list_data}
            name={`f_${field.id}`}
            id={`f_${field.id}`}
            key={`f_${field.id}`}
            multiple
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(e, v) => handleFilterChange(`f_${field.id}`, e, v)}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} label={field.field_template_name} />
            )}
          />
        ))}
        <button>Download</button>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {reportData.fieldsIncluded?.map((field) => (
                  <TableCell key={field.id}>
                    {field.field_template_name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableDataToShow &&
                tableDataToShow.map((td, i) => {
                  const fieldsArr = orderFieldsAndSanitizeValues(
                    td,
                    reportData
                  );
                  return (
                    <TableRow key={i + 1}>
                      {fieldsArr.map((field) => {
                        return <TableCell>{field}</TableCell>;
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </>
  );
}

function orderFieldsAndSanitizeValues(fields, reportData) {
  const sfArr = reportData.fieldsIncluded;
  let fieldsArr = [];
  for (let i = 0; i < sfArr.length; i++) {
    let k = Object.keys(fields).find((f) => f === "f_" + sfArr[i].id);

    let dataStructure = reportData.allFields.find(
      (f) => "f_" + f.id === k
    ).data_structure;

    if (dataStructure !== FIELD_TYPES.LIST) {
      fieldsArr.push(fields[k]);
    } else {
      fieldsArr.push(
        replaceListtDataIdWithListDataTextValue(fields[k], reportData)
      );
    }
  }
  return fieldsArr;
}

function replaceListtDataIdWithListDataTextValue(ldId, reportData) {
  return reportData.listData.find((lDatum) => lDatum.id == ldId)?.value || ldId;
}

function isFiltersEmpty(filters) {
  for (let [k, v] of Object.entries(filters)) {
    if (v.length > 0) {
      return false;
    }
  }

  return true;
}

export default ReportView;
